import React, { RefObject } from 'react';
import { Carousel } from '@mantine/carousel';
import { Box, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { usePostHog } from '@posthog/react';
import { Recipe, MealPlanAssignment, Alternative, getAssignmentMealType } from '../../types/mealPlan';
import { AnalyticsEvent } from '../../utils/analytics';

interface RecipeCarouselProps {
  selectedRecipe: Recipe;
  selectedReasoning: string;
  selectedSource: 'user_recipe' | 'ai_generated' | 'assignment_reference';
  alternatives: Alternative[];
  assignment: MealPlanAssignment;
  isOwner: boolean;
  isDraft: boolean;
  disabled?: boolean; // Prevents navigation while substitution is in progress
  hidden?: boolean; // Hide pointer events (when card is flipped)
  onSwap: (alternativeRecipe: Recipe, swapSequence: number, targetRecipeIdRef: RefObject<string>) => Promise<void>;
  // Render prop pattern for recipe content
  // source is the recipe's source for badge display (optimistic UI)
  // isCarousel indicates this is being rendered inside a carousel (for tour highlighting)
  renderRecipeContent: (
    recipe: Recipe,
    isSelected: boolean,
    reasoning?: string,
    source?: 'user_recipe' | 'ai_generated' | 'assignment_reference',
    isCarousel?: boolean
  ) => React.ReactNode;
  alternativesOrder?: string[]; // Fixed carousel order
  demoMode?: boolean; // Demo mode for landing page - disables swap API calls
  // Ref to register pending swap flush callbacks (keyed by assignment id)
  // for activation coordination
  pendingSwapCallbacksRef?: React.RefObject<Map<string, () => Promise<void>>>;
  // Fires immediately on slide change for optimistic prep day preview (no debounce)
  onPreviewRecipe?: (recipe: Recipe | null) => void;
}

export const RecipeCarousel: React.FC<RecipeCarouselProps> = ({
  selectedRecipe,
  selectedReasoning,
  selectedSource,
  alternatives,
  assignment,
  isOwner,
  isDraft,
  disabled,
  hidden,
  onSwap,
  renderRecipeContent,
  alternativesOrder,
  demoMode = false,
  pendingSwapCallbacksRef,
  onPreviewRecipe,
}) => {
  // Build recipe maps
  const { allRecipes, reasoningMap, sourceMap } = React.useMemo(() => {
    // Create reasoning map: recipeId -> reasoning
    const reasoningMap = new Map<string, string>();
    reasoningMap.set(selectedRecipe.id, selectedReasoning);
    alternatives.forEach(alt => {
      if (alt.reasoning) {
        reasoningMap.set(alt.recipe.id, alt.reasoning);
      }
    });

    // Create source map: recipeId -> source (for optimistic UI updates)
    const sourceMap = new Map<string, 'user_recipe' | 'ai_generated' | 'assignment_reference'>();
    sourceMap.set(selectedRecipe.id, selectedSource);
    alternatives.forEach(alt => {
      sourceMap.set(alt.recipe.id, alt.source);
      // Propagate isCurated/isTrending from Alternative onto recipe for
      // immediate display
      if (alt.isCurated) {
        alt.recipe.isCurated = true;
      }
      if (alt.isTrending) {
        alt.recipe.isTrending = true;
      }
    });

    let orderedRecipes: Recipe[];
    if (!alternativesOrder) {
      // Backward compatibility: no order specified
      orderedRecipes = [selectedRecipe, ...alternatives.map(alt => alt.recipe)];
    } else {
      // Use alternativesOrder to determine slide order
      const recipeMap = new Map<string, Recipe>();
      recipeMap.set(selectedRecipe.id, selectedRecipe);
      alternatives.forEach(alt => recipeMap.set(alt.recipe.id, alt.recipe));

      orderedRecipes = alternativesOrder
        .map(id => recipeMap.get(id))
        .filter((recipe): recipe is Recipe => recipe !== undefined);
    }

    return { allRecipes: orderedRecipes, reasoningMap, sourceMap };
  }, [selectedRecipe, selectedReasoning, selectedSource, alternatives, alternativesOrder]);

  // Find the index of the selected recipe in the ordered slides
  const selectedRecipeIndex = React.useMemo(() => {
    return allRecipes.findIndex(recipe => recipe.id === selectedRecipe.id);
  }, [allRecipes, selectedRecipe.id]);

  // Track the most recent navigation with a sequence number to ensure only latest processes
  const targetRecipeIdRef = React.useRef<string>(selectedRecipe.id);
  const navigationSequenceRef = React.useRef<number>(0);
  const swapSequenceRef = React.useRef<number>(0); // Track which swap we're actually executing
  const swapTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [currentSlide, setCurrentSlide] = React.useState(selectedRecipeIndex);
  const [emblaApi, setEmblaApi] = React.useState<any>(null);
  const posthog = usePostHog();

  // Update current slide when selected recipe changes
  React.useEffect(() => {
    setCurrentSlide(selectedRecipeIndex);
  }, [selectedRecipeIndex]);

  // Force Embla to reinitialize when assignment moves to a different day or disabled state changes
  // This ensures the carousel's internal slide-to-DOM-element mapping is correct after drag-and-drop
  // and that dragging is properly enabled/disabled during substitutions
  React.useEffect(() => {
    if (!emblaApi) return;

    // Reinitialize Embla to fix its internal DOM element mapping and pick up watchDrag changes
    emblaApi.reInit({ watchDrag: !disabled });

    // Then scroll to the correct position
    if (selectedRecipeIndex >= 0) {
      emblaApi.scrollTo(selectedRecipeIndex, false);
    }
  }, [emblaApi, assignment.plannedDay, selectedRecipeIndex, disabled]);

  // Helper to execute a pending swap immediately (used by flush and normal timer)
  const executePendingSwap = React.useCallback(() => {
    const targetRecipeId = targetRecipeIdRef.current;
    if (targetRecipeId !== selectedRecipe.id) {
      const targetRecipe = allRecipes.find(r => r.id === targetRecipeId);
      if (targetRecipe) {
        swapSequenceRef.current += 1;
        posthog?.capture(AnalyticsEvent.AlternativeSelected, {
          planned_day: assignment.plannedDay,
          meal_type: getAssignmentMealType(assignment),
          recipe_id: targetRecipe.id,
        });
        return onSwap(targetRecipe, swapSequenceRef.current, targetRecipeIdRef);
      }
    }
    return Promise.resolve();
  }, [selectedRecipe.id, allRecipes, onSwap, posthog, assignment]);

  // Clean up swap timeout and flush callback on unmount
  React.useEffect(() => {
    const callbacksMap = pendingSwapCallbacksRef?.current;
    return () => {
      if (swapTimeoutRef.current) {
        clearTimeout(swapTimeoutRef.current);
        swapTimeoutRef.current = null;
      }
      callbacksMap?.delete(assignment.id);
    };
  }, [assignment.id, pendingSwapCallbacksRef]);

  // Handle slide change - track most recent target, process only after settling
  const handleSlideChange = (index: number) => {
    const recipe = allRecipes[index];
    if (!recipe) return;

    // Update current slide for indicators
    setCurrentSlide(index);

    // Increment sequence number for this navigation
    navigationSequenceRef.current += 1;
    const currentSequence = navigationSequenceRef.current;

    // Update target to most recent navigation (overwrites previous)
    targetRecipeIdRef.current = recipe.id;

    // Clear any pending swap timer
    if (swapTimeoutRef.current) {
      clearTimeout(swapTimeoutRef.current);
      swapTimeoutRef.current = null;
    }

    // Immediately notify for optimistic prep day preview
    onPreviewRecipe?.(recipe.id === selectedRecipe.id ? null : recipe);

    // If navigating back to the already-selected recipe, no swap needed
    if (recipe.id === selectedRecipe.id) {
      pendingSwapCallbacksRef?.current?.delete(assignment.id);
      return;
    }

    // Funnel instrumentation: user browsed to an alternative for this day
    if (!demoMode) {
      posthog?.capture(AnalyticsEvent.AlternativeViewed, {
        planned_day: assignment.plannedDay,
        meal_type: getAssignmentMealType(assignment),
        recipe_id: recipe.id,
      });
    }

    // Wait for user to settle (300ms of no navigation)
    // Note: Don't check !isSwapping - we want to queue the next swap even if one is in flight
    // Skip swap in demo mode - carousel is just for visual browsing
    if (isOwner && isDraft && !demoMode) {
      // Register flush callback so activation can trigger the swap immediately
      pendingSwapCallbacksRef?.current?.set(assignment.id, async () => {
        if (swapTimeoutRef.current) {
          clearTimeout(swapTimeoutRef.current);
          swapTimeoutRef.current = null;
        }
        pendingSwapCallbacksRef?.current?.delete(assignment.id);
        await executePendingSwap();
      });

      swapTimeoutRef.current = setTimeout(() => {
        // Only process if this is still the most recent navigation
        if (currentSequence === navigationSequenceRef.current) {
          pendingSwapCallbacksRef?.current?.delete(assignment.id);
          executePendingSwap();
        }

        swapTimeoutRef.current = null;
      }, 1000);
    }
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box style={{ flex: 1 }}>
        <Carousel
          key={assignment.id} // Stable key prevents carousel from resetting on re-renders
          withIndicators={false}
          withControls={allRecipes.length > 1}
          slideSize="100%"
          slideGap="md"
          initialSlide={selectedRecipeIndex}
          onSlideChange={handleSlideChange}
          getEmblaApi={setEmblaApi}
          draggable={!disabled}
          emblaOptions={{
            loop: true,
            // In demo mode, only allow drag on the image area
            watchDrag: demoMode
              ? (_emblaApi, event) => {
                  const target = event.target as HTMLElement;
                  return target.closest('[data-carousel-drag-area="true"]') !== null;
                }
              : (_emblaApi, event) => {
                  // Don't start a swipe when the gesture begins on an interactive
                  // control (heart, info, menu, action buttons) - those stay
                  // clickable rather than being swallowed by the carousel drag.
                  const target = event.target as HTMLElement;
                  return !target.closest('button, a, [role="button"], [data-no-carousel-drag="true"]');
                },
          }}
          styles={{
            // Disable text selection so dragging over the title/description/macros
            // swipes smoothly instead of starting a native text selection.
            root: {
              userSelect: 'none',
              WebkitUserSelect: 'none',
            },
            controls: {
              top: 100, // Position on the image (200px image height / 2)
              transform: 'translateY(-50%)',
            },
            control: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              border: 'none',
              color: 'white',
              opacity: disabled ? 0.3 : 1,
              pointerEvents: disabled ? 'none' : 'auto',
              // Apple HIG recommended minimum touch target size
              width: 44,
              height: 44,
              minWidth: 44,
              minHeight: 44,
            },
          }}
          nextControlIcon={<IconChevronRight size={24} />}
          previousControlIcon={<IconChevronLeft size={24} />}
        >
      {allRecipes.map((recipe, index) => {
        // Show menu/reasoning for the currently visible slide, not based on backend state
        const isSelected = index === currentSlide;
        const reasoning = reasoningMap.get(recipe.id);
        // Get the source for this recipe (for optimistic badge display)
        const source = sourceMap.get(recipe.id);

        return (
          <Carousel.Slide key={recipe.id}>
            {/* Render recipe content via render prop */}
            {renderRecipeContent(recipe, isSelected, reasoning, source, true)}
          </Carousel.Slide>
        );
      })}
        </Carousel>
      </Box>

      {/* Custom indicators at consistent bottom position, plus a labeled
          affordance — dots alone read as an image gallery, so say what they mean.
          The label sits on its own centered line above the dots so it never
          crowds the flip/nutrition action icons absolutely positioned at the
          card's bottom-right on narrow screens. */}
      <Stack gap={10} align="center" py={8} style={{ pointerEvents: hidden ? 'none' : 'auto', position: 'relative', zIndex: 2 }}>
        {allRecipes.length > 1 && !demoMode && (
          <UnstyledButton
            onClick={(e) => {
              e.stopPropagation();
              if (emblaApi && !disabled && !hidden) {
                emblaApi.scrollTo((currentSlide + 1) % allRecipes.length);
                if (!demoMode) {
                  posthog?.capture(AnalyticsEvent.MoreIdeasChipClicked, {
                    planned_day: assignment.plannedDay,
                  });
                }
              }
            }}
            style={{
              cursor: disabled || hidden ? 'default' : 'pointer',
              pointerEvents: hidden ? 'none' : 'auto',
            }}
          >
            <Group gap={2} wrap="nowrap">
              <Text size="xs" fw={600} c="orange.7" lh={1}>
                {allRecipes.length - 1} more idea{allRecipes.length - 1 !== 1 ? 's' : ''}
              </Text>
              <IconChevronRight size={12} color="var(--mantine-color-orange-7)" />
            </Group>
          </UnstyledButton>
        )}
        <Group justify="center" gap="xs">
          {allRecipes.map((recipe, index) => (
            <Box
              key={recipe.id}
              onClick={(e) => {
                e.stopPropagation();
                if (emblaApi && !disabled && !hidden) {
                  emblaApi.scrollTo(index);
                }
              }}
              style={{
                width: currentSlide === index ? 16 : 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: currentSlide === index ? 'var(--mantine-color-orange-6)' : 'rgba(0, 0, 0, 0.3)',
                transition: 'width 250ms ease, background-color 250ms ease',
                cursor: (disabled || hidden) ? 'default' : 'pointer',
                pointerEvents: hidden ? 'none' : 'auto',
              }}
            />
          ))}
        </Group>
      </Stack>
    </Box>
  );
};
